import { _objectEntries, StringMap } from '@naturalcycles/js-lib'

// Styles we don't want to copy from the original element to the wrapper
const problematicStyles = [
  // We don't want after to be rotating and stuff according to the parent
  /^transform$/,
  /^animation$/,
  /^transition$/,
  // We need the inspection text to be visible
  /^opacity$/,
  /^visibility$/,
  // These override the color property and we don't really need them
  /^-webkit-text-.*/,
]

/**
 * Wraps an element with a wrapper element that has a qa attribute. It
 * also copies the styles from the original element to the wrapper to
 * try to keep the appearance as close as possible. Finally, it replaces
 * the original element with the wrapper.
 */
const handleWrappable = <T extends Element>(
  node: T,
  wrapper: string,
  qaAttr: string,
  getVal: (node: T) => string,
): void => {
  const value = getVal(node)
  if (!value) return
  if (node.parentElement?.tagName === wrapper.toUpperCase()) {
    node.parentElement.setAttribute(qaAttr, value)
  } else {
    const wrapperEl = document.createElement(wrapper)
    const styles = getComputedStyle(node)
    for (const prop of styles) {
      if (prop && !problematicStyles.some(s => s.test(prop))) {
        wrapperEl.style.setProperty(prop, styles.getPropertyValue(prop))
      }
    }
    wrapperEl.setAttribute(qaAttr, value)
    wrapperEl.appendChild(node.cloneNode(true))
    node.parentElement?.replaceChild(wrapperEl, node)
  }
}

/**
 * Attempts to find a matching translation key for a given text.
 *
 * If none are found, it tries to match the text line by line, which is
 * effective for text from html strings or markdown.
 */
export const getMatchingTranslationKey = (text: string, translations: StringMap): string => {
  if (text.match(/^[\s\t\n]*$/)) return ''
  text = text.trim()
  const translationEntries = _objectEntries(translations)
  let matches = translationEntries.filter(([_, value]) => value === text.trim()).map(([key]) => key)
  if (!matches.length) {
    matches = translationEntries
      .filter(([_, value]) =>
        text
          .split('\n')
          .map(t => t.trim())
          .filter(Boolean)
          .every(t => value?.includes(t)),
      )
      .map(([key]) => key)
  }
  return matches.join(',\n')
}

const handleImage = (node: HTMLImageElement): void => {
  handleWrappable<HTMLImageElement>(node, 'picture', 'qa-url', n =>
    n.src.replace(new RegExp(`^${location.origin}`), ''),
  )
}

const handleLabel = (node: HTMLLabelElement, translations: StringMap): void => {
  handleWrappable<HTMLLabelElement>(node, 'div', 'translate-key', n =>
    getMatchingTranslationKey(n.textContent || '', translations),
  )
}

const handleText = (node: Element, translations: StringMap): void => {
  handleWrappable<Element>(node, 'span', 'translate-key', n =>
    getMatchingTranslationKey(n.textContent || '', translations),
  )
}

// Shadow doms don't play well with the inspector
const ignoreSelectors = ['.carouselSlider__slide', 'swiper-slide']

// Utility that determines which type of element we're dealing with and calls the appropriate handler
const handleNode = (node: Element, translations: StringMap): void => {
  // We don't want a text node to have inspectable children
  const allowedTextChildren = ['BR']
  if (!(node instanceof Element) || ignoreSelectors.some(s => node.matches(s))) {
    return
  }
  if (node instanceof HTMLImageElement) {
    handleImage(node)
  } else if (node instanceof HTMLLabelElement) {
    handleLabel(node, translations)
  } else if (
    node.textContent?.length &&
    [...node.children].every(c => allowedTextChildren.includes(c.nodeName))
  ) {
    handleText(node, translations)
  }
}

/**
 * Recursively inspects the dom and wraps elements with qa attributes, or adds qa attributes to them,
 * depending on the type of element.
 *
 * @param node The root node to start inspecting from
 * @param translations A map of translations to match text against
 */
export const recInspect = (node: Element, translations: StringMap): void => {
  const children = [...(node?.children || [])]
  children.forEach(child => recInspect(child, translations))
  handleNode(node, translations)
}
